import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import { useState } from "react";
import VenueAutocomplete from "../../bookings/steps/components/VenueAutoComplete";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Venue } from "../../../types/venue";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { Close } from "@mui/icons-material";
import VenueDialog from "../../bookings/steps/components/AddVenueDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  quoteId: number;
}

interface ConvertToBookingDialogResponse {
  booking_id: number;
}

const EMPTY_VENUE: Venue = {
  name: "",
  address_one: "",
  address_two: "",
  suburb: "",
  city: "",
};

const ConvertToBookingDialog: React.FC<Props> = ({
  open,
  onClose,
  quoteId,
}) => {
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const navigate = useNavigate();

  const [openAddVenueDialog, setOpenAddVenueDialog] = useState(false);
  const [newVenue, setNewVenue] = useState<Venue>(EMPTY_VENUE);
  const [addVenueLoading, setAddVenueLoading] = useState(false);

  const handleOpenAddVenueDialog = () => {
    setOpenAddVenueDialog(true);
  };

  const handleCloseAddVenueDialog = () => {
    setNewVenue(EMPTY_VENUE);
    setOpenAddVenueDialog(false);
  };

  const handleVenueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVenue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddVenue = async () => {
    setAddVenueLoading(true);
    try {
      const response = await apiService.post<Venue>(
        "/api/venues/venue/",
        newVenue,
      );

      setVenueOptions((prev) => [...prev, response.data]);

      setSelectedVenue(response.data);

      toast.success("Venue added successfully");
      setNewVenue(EMPTY_VENUE);
      handleCloseAddVenueDialog();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to add venue");
      }
    } finally {
      setAddVenueLoading(false);
    }
  };

  const fetchVenues = async (search: string) => {
    if (search.length < 3) return;
    setVenueLoading(true);
    try {
      const response = await apiService.get<{ results: Venue[] }>(
        `/api/venues/venue/?search=${search}`,
      );
      setVenueOptions(response.data.results || []);
    } finally {
      setVenueLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedVenue) return;

    setConverting(true);
    try {
      const response = await apiService.post<ConvertToBookingDialogResponse>(
        `/api/bookings/quote-request/${quoteId}/convert-to-booking/`,
        {
          venue_id: selectedVenue.id,
        },
      );

      toast.success("Quote converted to booking successfully");
      onClose();
      navigate(`/bookings/${response.data.booking_id}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "Failed to convert quote to booking";
        toast.error(errorMessage);
      } else {
        console.error("Failed to fetch member", error);
      }
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>Select Venue</DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 2 }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        <VenueAutocomplete
          value={selectedVenue}
          options={venueOptions}
          loading={venueLoading}
          onChange={(_, value) => setSelectedVenue(value)}
          onInputChange={(_, value) => fetchVenues(value)}
          onAddVenueClick={handleOpenAddVenueDialog}
          required
          theme={reduxTheme}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedVenue || converting || addVenueLoading}
          onClick={handleConvert}
        >
          Convert to Booking
        </Button>
      </DialogActions>

      <VenueDialog
        open={openAddVenueDialog}
        onClose={handleCloseAddVenueDialog}
        onSubmit={handleAddVenue}
        loading={addVenueLoading}
        newVenue={newVenue}
        onChange={handleVenueInputChange}
        cities={reduxTheme.cities}
        theme={reduxTheme}
      />
    </Dialog>
  );
};

export default ConvertToBookingDialog;
